import * as React from "react";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {DeleteProject, getProject, updateProject,} from "../BackendFacade/apiCalls";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import {odxIconSets, odxVariants} from "../../../OdxComponents/constants";
import {IProjectInformation_Edit} from "../Interfaces/IProjectInformation_Edit";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import DeleteProjectModal from "./Modals/DeleteProjectModal";
import OdxInputField from "../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import OdxChip from "../../../OdxComponents/NativeOdx/Odx-Chip";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {IContact} from "../../Hub/Interfaces/IContact";
import {simplifyForHtmlId} from "../../../Utils/transformer";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";

const ProjectInformation: React.FC = () => {
    const [description, setDescription] = useState<string>("");
    const [contacts, setContacts] = useState<IContact[]>([]);
    const [costCenter, setCostCenter] = useState<number>(0);
    const [department, setDepartment] = useState<string>("");
    const [product, setProduct] = useState<string>("");
    const [customProjectIcon, setCustomProjectIcon] = useState<string>("");
    const [internalOrderNumber, setInternalOrderNumber] = useState<number>(0);
    const [title, setTitle] = useState<string>(
        HubPathRouting.currentProject.displayName
    );
    const [newContactEmail, setNewContactEmail] = useState<string>("");
    const [newContactJob, setNewContactJob] = useState<string>(""); // New state for job title
    const [isAddingContact, setIsAddingContact] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [isApplyingProjectInfo, setIsApplyingProjectInfo] = useState<boolean>(false);
    const [sourceBy, setSourceBy] = useState<string>("");
    const baseId = 'project-info';

    const [oldProjectState, setOldProjectState] = useState<IProjectInformation_Edit>(undefined);

    const {setLoading, createToast, createErrorToast} = useMain();
    const navigate = useNavigate();

    useModuleViewTracking('project-info')

    useEffect(() => {
        const currentProject = HubPathRouting.currentProject;
        currentProject.displayName = title;
        HubPathRouting.currentProject = currentProject;
    }, [title]);

    useEffect(() => {
        try {
            HubPathRouting.subscribe(() => {
                if (
                    HubPathRouting.currentProject &&
                    HubPathRouting.currentProject.displayName
                )
                    setTitle(HubPathRouting.currentProject.displayName);
            });
        } catch (e) {
            // do nothing, project was deleted
        }

        if (
            !GlobalDictionary.get(
                GlobalDictionary.PROJECTINFO + HubPathRouting.currentProject.id
            )
        ) {
            setLoading(true);
            getProject(HubPathRouting.currentProject.id).then(
                (data) => {
                    if (data.success) {
                        let project = data.data.value;
                        GlobalDictionary.set(
                            GlobalDictionary.PROJECTINFO +
                            HubPathRouting.currentProject.id,
                            project
                        );
                        prepareProjectInfo();
                    } else {
                        createErrorToast(data.message);
                    }
                    setLoading(false);
                }
            );
        } else {
            prepareProjectInfo();
        }
    }, []);

    // save contacts after a new contact is set
    useEffect(() => {
        if (isApplyingProjectInfo) {
            applyProject().then(() => {
                setIsApplyingProjectInfo(false);
            });
        }
    }, [isApplyingProjectInfo]);

    const prepareProjectInfo = () => {
        let project = GlobalDictionary.get(
            GlobalDictionary.PROJECTINFO + HubPathRouting.currentProject.id
        );
        project = project as IProjectInformation_Edit;
        project.projectId = HubPathRouting.currentProject.id;
        setOldProjectState(project);
        setDescription(project.description);
        setContacts(project.contacts);
        setCostCenter(project.costCenter);
        setDepartment(project.department);
        setProduct(project.product);
        setCustomProjectIcon(project.customProjectIcon);
        setInternalOrderNumber(project.internalOrderNumber);
        setTitle(project.displayName);
        setSourceBy(project.source);
    };

    const handleImageSelect = () => {
        let fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*";
        fileInput.onchange = () => {
            let file = fileInput.files?.item(0);
            if (file === null || file === undefined) {
                return;
            }
            let reader = new FileReader();
            reader.onload = (event) => {
                const base64Image = event.target?.result as string;
                if (base64Image.length > 64 * 1024) {
                    setCustomProjectIcon("");
                    createErrorToast("The selected image is larger than 64KB. Please choose a smaller image.");
                } else {
                    setCustomProjectIcon(base64Image);
                    applyProject(base64Image);
                }
            };
            reader.readAsDataURL(file);
        };
        fileInput.click();
    };

    const deleteImage = () => {
        setCustomProjectIcon("");
        applyProject("-1");
    };

    const ProjectIcon = () => {
        if (customProjectIcon === "" || customProjectIcon === null) return <></>;
        return (
            <img
                src={customProjectIcon}
                alt={"Project Icon"}
                className="w-[50px] h-[50px] mr-[10px] max-w-[150px] max-h-[150px]"
                id={`${baseId}-icon-image`}
            />
        );
    };

    const addContactToList = () => {
        if (newContactEmail === "") {
            return;
        }
        const contactsTmp = [...contacts];
        // Add both mail and job (if provided; if not, set to null)
        contactsTmp.push({mail: newContactEmail, job: newContactJob ? newContactJob : null});
        setContacts(contactsTmp);
        setNewContactEmail("");
        setNewContactJob("");
        setIsAddingContact(false);
        setIsApplyingProjectInfo(true);
    };

    const removeContact = (index: number) => {
        const contactsTmp = [...contacts];
        contactsTmp.splice(index, 1);
        setContacts(contactsTmp);
        setIsApplyingProjectInfo(true);
    };

    const applyProject = async (projectIcon?: string) => {
        if (
            !isUserroleSufficient(
                UserRoles.editor,
                HubPathRouting.currentProject?.id
            )
        ) {
            return;
        }

        let icon = projectIcon;
        if (projectIcon === "-1") {
            icon = "";
        }

        if (!projectIcon) {
            icon = customProjectIcon;
        }
        const state = {
            projectId: HubPathRouting.currentProject.id,
            displayName: title,
            description: description,
            contacts: contacts,
            costCenter: costCenter,
            department: department,
            product: product,
            customProjectIcon: icon,
            internalOrderNumber: internalOrderNumber,
        } as IProjectInformation_Edit;

        const isNotUpdate= Object.keys(state).every((key) => {
            if (state[key] === oldProjectState[key]) {
                return true;
            }
            return false
        })
        if (isNotUpdate) {
            return;
        }

        await updateProject(state)
            .then((data) => {
                if (data.success) {
                    GlobalDictionary.set(
                        GlobalDictionary.PROJECTINFO + HubPathRouting.currentProject.id,
                        state
                    );
                    prepareProjectInfo();
                    createToast({
                        message: "Project information updated successfully.",
                        variant: "success",
                        title: "Success",
                        duration: 2000,
                    });
                } else {
                    createErrorToast(data.message);
                }
            })
            .catch((error) => {
                createErrorToast("Failed to save project");
            });
    };

    const deleteProject = (
        projectId: string,
        hardDelete: boolean = false
    ) => {
        setLoading(true);
        DeleteProject(projectId, hardDelete).then((data) => {
            if (data.success) {
                GlobalDictionary.remove(GlobalDictionary.PROJECTS);
                navigate("/");
            } else {
                createErrorToast(data.message);
            }
            setLoading(false);
        });
    };

    const handleDelete = (deleteType: "hard" | "soft") => {
        const hardDelete = deleteType === "hard";
        deleteProject(HubPathRouting.currentProject.id, hardDelete);
    };

    const openDeleteModal = () => {
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    return (
        <>
            {isDeleteModalOpen && (
                <DeleteProjectModal
                    isOpen={isDeleteModalOpen}
                    onClose={closeDeleteModal}
                    onDelete={handleDelete}
                    projectName={title}
                />
            )}

            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>

            <OdxAreaHeader text="Project Info" odxSize="medium" className="pl-0" id={`${baseId}-header`}/>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" id={`${baseId}-grid`}>
                <div id={`${baseId}-basic`}>
                <OdxContentBox title="Basic Info">
                    <div>
                        <div className="flex items-start" id={`${baseId}-icon-row`}>
                            <div className="min-w-[80px] min-h-[80px]" id={`${baseId}-icon-wrapper`}>{ProjectIcon()}</div>
                            <div className="flex flex-col ml-4">
                                <div className="mb-2" id={`${baseId}-upload-wrapper`}>
                                    <>
                                        {isUserroleSufficient(UserRoles.admin, HubPathRouting.currentProject?.id) && (
                                            <odx-button
                                                variant="ghost"
                                                size="lg"
                                                type="button"
                                                id={`${baseId}-upload-image-btn`}
                                                onClick={handleImageSelect}
                                                disabled={
                                                    !isUserroleSufficient(
                                                        UserRoles.editor,
                                                        HubPathRouting.currentProject?.id
                                                    )
                                            }
                                        >
                                                <odx-icon slot="prefix" name={`${odxIconSets.core}::upload`} size="md"></odx-icon>
                                                Upload Image
                                            </odx-button>
                                        )}
                                    </>
                                </div>
                                <div className="mb-2" id={`${baseId}-remove-wrapper`}>
                                    <odx-button
                                        variant="ghost"
                                        size="lg"
                                        type="button"
                                        id={`${baseId}-remove-image-btn`}
                                        onClick={deleteImage}
                                        disabled={
                                            !isUserroleSufficient(
                                                UserRoles.editor,
                                                HubPathRouting.currentProject?.id
                                            )
                                        }
                                    >
                                        <odx-icon slot="prefix" name={`${odxIconSets.core}::delete`} size="md"></odx-icon>
                                        Remove Image
                                    </odx-button>
                                </div>
                            </div>
                        </div>
                        <odx-separator style={{margin: '1rem 0'}} />
                        <OdxInputField
                            label="Display Name"
                            value={title}
                            id={`${baseId}-display-name`}
                            isRequired={true}
                            onChange={(event) => setTitle(event.target.value)}
                            onBlur={() => applyProject()}
                            isDisabled={
                                !isUserroleSufficient(
                                    UserRoles.editor,
                                    HubPathRouting.currentProject?.id
                                )
                            }
                        />

                        <OdxInputField
                            label="Description"
                            value={description}
                            id={`${baseId}-description`}
                            isRequired={false}
                            onChange={(event) => setDescription(event.target.value)}
                            onBlur={() => applyProject()}
                            isDisabled={
                                !isUserroleSufficient(
                                    UserRoles.editor,
                                    HubPathRouting.currentProject?.id
                                )
                            }
                        />
                        <OdxChip size={"medium"} content={sourceBy} variant={odxVariants.highlight} id={`${baseId}-source-chip`} />
                    </div>
                </OdxContentBox>
                </div>

                <div id={`${baseId}-cost`}>
                <OdxContentBox title="Cost Info" className="p-4">
                    <OdxInputField
                        label="Cost Center"
                        value={costCenter ? costCenter.toString() : ""}
                        id={`${baseId}-cost-center`}
                        isRequired={true}
                        onChange={(event) => setCostCenter(Number(event.target.value))}
                        onBlur={() => applyProject()}
                        isDisabled={
                            !isUserroleSufficient(
                                UserRoles.editor,
                                HubPathRouting.currentProject?.id
                            )
                        }
                    />

                    <OdxInputField
                        label="Internal Order Number"
                        value={
                            internalOrderNumber ? internalOrderNumber.toString() : ""
                        }
                        id={`${baseId}-internal-order`}
                        isRequired={false}
                        onChange={(event) =>
                            setInternalOrderNumber(Number(event.target.value))
                        }
                        onBlur={() => applyProject()}
                        isDisabled={
                            !isUserroleSufficient(
                                UserRoles.editor,
                                HubPathRouting.currentProject?.id
                            )
                        }
                    />

                    <OdxInputField
                        label="Department"
                        value={department}
                        id={`${baseId}-department`}
                        isRequired={true}
                        onChange={(event) => setDepartment(event.target.value)}
                        onBlur={() => applyProject()}
                        isDisabled={
                            !isUserroleSufficient(
                                UserRoles.editor,
                                HubPathRouting.currentProject?.id
                            )
                        }
                    />

                    <OdxInputField
                        label="Product"
                        value={product}
                        id={`${baseId}-product`}
                        isRequired={true}
                        onChange={(event) => setProduct(event.target.value)}
                        onBlur={() => applyProject()}
                        isDisabled={
                            !isUserroleSufficient(
                                UserRoles.editor,
                                HubPathRouting.currentProject?.id
                            )
                        }
                    />
                </OdxContentBox>
                </div>

                <div id={`${baseId}-contacts`}>
                <OdxContentBox
                    title="Contacts"
                    className="p-4"
                    action={
                        <div className="flex justify-end items-start space-x-4">
                            {!isAddingContact && (
                                <odx-button
                                    variant="ghost"
                                    size="lg"
                                    type="button"
                                    id={`${baseId}-add-contact-btn`}
                                    onClick={() => setIsAddingContact(true)}
                                    disabled={
                                        !isUserroleSufficient(
                                            UserRoles.editor,
                                            HubPathRouting.currentProject?.id
                                        )
                                    }
                                >
                                    <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                    Add Email
                                </odx-button>
                            )}
                        </div>
                    }
                >
                    {isAddingContact && (
                        <div className="flex items-center mt-4" id={`${baseId}-new-contact-form`}>
                            <OdxInputField
                                label="Email Address"
                                value={newContactEmail}
                                id={`${baseId}-new-contact-email`}
                                isRequired={true}
                                onChange={(event) =>
                                    setNewContactEmail(event.target.value)
                                }
                                className="mr-2"
                            />
                            <OdxInputField
                                label="Job Title"
                                value={newContactJob}
                                id={`${baseId}-new-contact-job`}
                                isRequired={false}
                                onChange={(event) =>
                                    setNewContactJob(event.target.value)
                                }
                                className="mr-2"
                            />
                            <odx-button
                                variant="accent"
                                size="lg"
                                type="button"
                                id={`${baseId}-new-contact-submit`}
                                onClick={addContactToList}
                            >
                                Submit
                            </odx-button>
                        </div>
                    )}
                    <div className="flex flex-wrap" id={`${baseId}-contacts-list`}>
                        {contacts.map((contact, index) => (
                            <OdxChip
                                key={index}
                                removable={isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id)}
                                size="medium"
                                id={`${baseId}-contact-${simplifyForHtmlId(contact.mail || `contact-${index}`)}`}
                                variant={odxVariants.secondary}
                                content={
                                    contact.job
                                        ? `${contact.mail} (${contact.job})`
                                        : contact.mail
                                }
                                onRemove={() => removeContact(index)}
                                truncateText={true}
                            />
                        ))}
                    </div>
                </OdxContentBox>
                </div>
            </div>

            {isUserroleSufficient(
                UserRoles.admin,
                HubPathRouting.currentProject?.id
            ) && (
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2" id={`${baseId}-delete-wrapper`}>
                    <odx-button
                        variant="danger"
                        size="lg"
                        type="button"
                        id={`${baseId}-delete-project-btn`}
                        onClick={openDeleteModal}
                    >
                        Delete Project
                    </odx-button>
                </div>
            )}
        </>
    );
};

export default ProjectInformation;
